// ============================================================
// API クライアント (fetch ベース、axios 不使用)
// ベースパス: /api
// ============================================================

import type {
  MeActiveEvent,
  User,
  OrganizationDetail,
  OrganizationInvite,
  OrganizationBranding,
  OrganizationLogoCropRect,
  UpdateOrganizationBrandingRequest,
  SystemAdminUserEntry,
  SystemAdminOrganizerDetail,
  SystemAdminOrgEntry,
  SystemAdminLiveEvent,
  SystemAdminUpcomingEvent,
  Event,
  EventParticipant,
  Match,
  RankingEntry,
  LeagueRankingSection,
  Player,
  OrgPlayerEntry,
  PlayerDetail,
  PlayerEventInfo,
  PlayerMatch,
  PlayerMyPageData,
  PlayerMyPageEventsPage,
  PointRanking,
  CategoryRankingEntry,
  SignupPageData,
  PublicEventGameTitleListResponse,
  PublicEventListResponse,
  PublicStatsResponse,
  EventPublicPageContent,
  UpdateEventPublicPageContentRequest,
  EventPublicPageMapPreviewResponse,
  PointCategory,
  PointRule,
  PrivateMatch,
  PrivateMatchPage,
  SwissRound,
  TournamentRound,
  LeagueWithParticipants,
  CreateEventRequest,
  UpdateEventRequest,
  UpdateCoinBonusRuntimeRequest,
  CreateMatchRequest,
  FinishMatchRequest,
  InheritableEvent,
  InheritableParticipantsResponse,
  ApiResponse,
  GameTitle,
  RankMatchSeason,
  RankMatchSeasonCreateRequest,
  RankMatchSeasonUpdateRequest,
  RankMatchSeasonPlayersResponse,
  RankMatchSeasonSummary,
  PlayerRankMatchStatus,
  RankMatchLeaderboardResponse,
  RankMatchSeasonEventEntry,
  PlayerMergePreview,
  PlayerMergeResult,
  PlayerMergeContext,
  RollbackPreview,
  RollbackResult,
  CoinBetLiveActionRequest,
  CoinAdjustmentRequest,
  CoinAdjustmentResponse,
  CoinLoanRequest,
  CoinLoanRepaymentRequest,
  CoinLoanResponse,
  CoinBetProposal,
  CoinBetProposalListResponse,
  CoinTransferPreviewResponse,
  CoinTransferRequest,
  CoinTransferResponse,
  CoinTransferCandidate,
  CoinTransactionListResponse,
  BenefitAwardListResponse,
  LuckyRouletteBulkResolveResponse,
  LuckyRouletteDrawListResponse,
  SelfServeMatchRequest,
  SelfServeMatchCreateRequest,
  SelfServeMatchCandidate,
  SelfServeMatchRequestListResponse,
  SelfServeMatchPreviewResponse,
  EventMatchAvoidancePair,
  EventParticipantGroup,
  EventMatchAvoidanceListResponse,
  EventParticipantGroupListResponse,
  UpdateParticipantMatchConstraintsRequest,
  UpdateParticipantMatchConstraintsResponse,
} from '../../../shared/types'
import { safeLocalStorage } from '@/lib/safeStorage'
import { useAppVersionStore } from '@/stores/appVersionStore'

const BASE = '/api'
const AUTH_TOKEN_KEY = 'auth_token'

// ── セッション切れ時のリダイレクト先振り分け ───────────────────────
// プレイヤー側のページから 401 を食らったら /player-login に、
// 主催者側からなら /login に飛ばす。ログイン画面自体での 401 は無視
// （リダイレクトループ防止）。
function redirectToLogin() {
  const path = window.location.pathname
  if (path === '/login' || path === '/player-login') return
  const isPlayerArea =
    path.startsWith('/player/') ||
    path.startsWith('/event/p/') ||
    path.startsWith('/p/') ||
    path.startsWith('/event-checkin/')
  const target = isPlayerArea ? '/player-login' : '/login'
  window.location.href = `${target}?session=expired`
}

// ── サーバー発行のリフレッシュトークンを localStorage に反映 ────────
// authStore は localStorage を参照して decodeToken するため、
// 直接 localStorage を書き換えるだけで次回 API 呼び出し以降は新トークンが使われる。
// 同セッション内の UI 表示（name 等）に即時反映したい場合は authStore.setToken も呼ぶ必要があるが、
// スライディング更新ではペイロードが変わらないので localStorage 更新のみで十分。
function consumeRefreshToken(res: Response) {
  const refreshed = res.headers.get('X-Refresh-Token')
  if (refreshed) {
    safeLocalStorage.setItem(AUTH_TOKEN_KEY, refreshed)
  }
}

// ── 共通 fetch ラッパー ──────────────────────────────────────

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  await useAppVersionStore.getState().ensureFreshForMutation(init?.method)

  const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // body がある場合のみ Content-Type を付与する
      // ボディなし POST に Content-Type: application/json を付けると
      // Fastify が空ボディを JSON としてパースしようとして 400 になる
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })
  consumeRefreshToken(res)
  if (res.status === 401) {
    safeLocalStorage.removeItem(AUTH_TOKEN_KEY)
    redirectToLogin()
    throw new ApiError(401, 'Unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.message ?? res.statusText, {
      remainingSecs: body.remainingSecs,
      errorCode:     body.errorCode ?? body.error,
      violations:    body.violations,
    })
  }
  // 204 No Content はボディなし（DELETE など）→ undefined を返す
  if (res.status === 204) {
    return undefined as T
  }
  const json: ApiResponse<T> = await res.json()
  return json.data
}

export class ApiError extends Error {
  remainingSecs?: number
  errorCode?:    string   // サーバー側 `error` フィールド（'EmailRequired' 等の機械可読コード）
  violations?:   { field: string; reason?: string }[]  // 開催中編集ガードの 409（F-15D-1）
  constructor(public statusCode: number, message: string, extra?: { remainingSecs?: number; errorCode?: string; violations?: { field: string; reason?: string }[] }) {
    super(message)
    this.name = 'ApiError'
    if (extra?.remainingSecs !== undefined) this.remainingSecs = extra.remainingSecs
    if (extra?.errorCode    !== undefined) this.errorCode     = extra.errorCode
    if (extra?.violations   !== undefined) this.violations    = extra.violations
  }
}

// ── Events ──────────────────────────────────────────────────

export const eventsApi = {
  list: () =>
    request<Event[]>('/events'),

  get: (uuid: string) =>
    request<Event>(`/events/${uuid}`),

  create: (body: CreateEventRequest) =>
    request<Event>('/events', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (uuid: string, body: UpdateEventRequest) =>
    request<Event>(`/events/${uuid}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  delete: (uuid: string) =>
    request<void>(`/events/${uuid}`, { method: 'DELETE' }),

  archive: (uuid: string) =>
    request<Event>(`/events/${uuid}/archive`, { method: 'POST' }),

  discordCleanup: (uuid: string) =>
    request<{ queued: true }>(`/events/${uuid}/discord/cleanup`, { method: 'POST' }),

  checkInPhase: (uuid: string) =>
    request<Event>(`/events/${uuid}/check-in-phase`, { method: 'POST' }),

  backToPreparing: (uuid: string) =>
    request<Event>(`/events/${uuid}/back-to-preparing`, { method: 'POST' }),

  start: (uuid: string) =>
    request<Event>(`/events/${uuid}/start`, { method: 'POST' }),

  close: (uuid: string) =>
    request<Event>(`/events/${uuid}/close`, { method: 'POST' }),

  finish: (uuid: string, mode?: 'finish' | 'interrupt') =>
    request<Event>(`/events/${uuid}/finish`, {
      method: 'POST',
      body: mode ? JSON.stringify({ mode }) : undefined,
    }),

  setVisibility: (uuid: string, isHidden: boolean) =>
    request<Event>(`/events/${uuid}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ isHidden }),
    }),

  setPaused: (uuid: string, isPaused: boolean) =>
    request<Event>(`/events/${uuid}/pause`, {
      method: 'PATCH',
      body: JSON.stringify({ isPaused }),
    }),

  updateCoinBonusRuntime: (uuid: string, body: UpdateCoinBonusRuntimeRequest) =>
    request<Event>(`/events/${uuid}/coin-bonus-runtime`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // 引き継ぎ元候補イベント一覧（EventForm のドロップダウン用）
  listInheritable: () =>
    request<InheritableEvent[]>('/events/inheritable'),

  // 引き継ぎ元の順位表（target の uuid を渡す）
  getInheritableParticipants: (uuid: string) =>
    request<InheritableParticipantsResponse>(`/events/${uuid}/inheritable-participants`),
}

// ── Event public page (イベント案内ページ編集コンテンツ) ──────────

export const eventPublicPageApi = {
  getContent: (uuid: string) =>
    request<{ content: EventPublicPageContent }>(`/events/${uuid}/public-page-content`),

  updateContent: (uuid: string, body: UpdateEventPublicPageContentRequest) =>
    request<{ content: EventPublicPageContent }>(`/events/${uuid}/public-page-content`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  mapPreview: (uuid: string, address: string) =>
    request<EventPublicPageMapPreviewResponse>(
      `/events/${uuid}/public-page-content/map-preview?address=${encodeURIComponent(address)}`,
    ),

  // カバー画像アップロード（multipart/form-data・JWT 必須）。更新後の content を返す
  uploadCover: async (uuid: string, file: Blob, filename = 'cover.jpg'): Promise<{ content: EventPublicPageContent }> => {
    const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
    const fd = new FormData()
    fd.append('file', file, filename)
    const res = await fetch(`${BASE}/events/${uuid}/public-page-content/cover`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        // Content-Type は FormData が自動付与（boundary 込み）
      },
      body: fd,
    })
    consumeRefreshToken(res)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? res.statusText)
    }
    const json: ApiResponse<{ content: EventPublicPageContent }> = await res.json()
    return json.data
  },

  deleteCover: (uuid: string) =>
    request<{ content: EventPublicPageContent }>(`/events/${uuid}/public-page-content/cover`, {
      method: 'DELETE',
    }),
}

// ── Public events (公開イベント一覧) ─────────────────────────────

export interface PublicEventListParams {
  q?: string
  dateFrom?: string
  dateTo?: string
  format?: string
  gameTitleId?: number
  includeFinished?: boolean
  limit?: number
  cursor?: string
}

function publicEventListQuery(params: PublicEventListParams = {}): string {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom)
  if (params.dateTo) qs.set('dateTo', params.dateTo)
  if (params.format) qs.set('format', params.format)
  if (params.gameTitleId !== undefined) qs.set('gameTitleId', String(params.gameTitleId))
  if (params.includeFinished !== undefined) qs.set('includeFinished', String(params.includeFinished))
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.cursor) qs.set('cursor', params.cursor)
  const query = qs.toString()
  return query ? `?${query}` : ''
}

export const publicEventsApi = {
  list: (params?: PublicEventListParams) =>
    request<PublicEventListResponse>(`/public/events${publicEventListQuery(params)}`),

  listGameTitles: (params?: { includeFinished?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.includeFinished !== undefined) qs.set('includeFinished', String(params.includeFinished))
    const query = qs.toString()
    return request<PublicEventGameTitleListResponse>(`/public/event-game-titles${query ? `?${query}` : ''}`)
  },
}

export const publicStatsApi = {
  get: () => request<PublicStatsResponse>('/public/stats'),
}

// ── Rollback (巻戻し) ────────────────────────────────────────
// docs/design_event_rollback.md 参照

export const rollbackApi = {
  preview: (uuid: string) =>
    request<RollbackPreview>(`/events/${uuid}/rollback-preview`),

  execute: (uuid: string, confirmToken: string) =>
    request<RollbackResult>(`/events/${uuid}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ confirmToken }),
    }),
}

// ── Participants ─────────────────────────────────────────────

export const participantsApi = {
  list: (eventUuid: string) =>
    request<EventParticipant[]>(`/events/${eventUuid}/participants`),

  getCoinTransactions: (eventUuid: string, params: { participantId?: number; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.participantId !== undefined) query.set('participantId', String(params.participantId))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    return request<CoinTransactionListResponse>(`/events/${eventUuid}/coin-transactions${qs ? `?${qs}` : ''}`)
  },

  getBenefitAwards: (eventUuid: string, params: { participantId?: number; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.participantId !== undefined) query.set('participantId', String(params.participantId))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    return request<BenefitAwardListResponse>(`/events/${eventUuid}/benefit-awards${qs ? `?${qs}` : ''}`)
  },

  resolvePendingLuckyRouletteDraws: (eventUuid: string, participantId: number) =>
    request<LuckyRouletteBulkResolveResponse>(
      `/events/${eventUuid}/participants/${participantId}/lucky-roulette-draws/resolve-pending`,
      { method: 'POST' },
    ),

  add: (eventUuid: string, body: { name: string; playerId?: number }) =>
    request<EventParticipant>(`/events/${eventUuid}/participants`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (eventUuid: string, id: number, body: { name?: string }) =>
    request<EventParticipant>(`/events/${eventUuid}/participants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adjustCoins: (eventUuid: string, id: number, body: CoinAdjustmentRequest) =>
    request<CoinAdjustmentResponse>(`/events/${eventUuid}/participants/${id}/coin-adjustments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  loanCoins: (eventUuid: string, id: number, body: CoinLoanRequest) =>
    request<CoinLoanResponse>(`/events/${eventUuid}/participants/${id}/coin-loans`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  repayCoinLoan: (eventUuid: string, id: number, body: CoinLoanRepaymentRequest) =>
    request<CoinLoanResponse>(`/events/${eventUuid}/participants/${id}/coin-loan-repayments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  remove: (eventUuid: string, id: number) =>
    request<void>(`/events/${eventUuid}/participants/${id}`, {
      method: 'DELETE',
    }),

  changeStatus: (
    eventUuid: string,
    id: number,
    body: { status: string; tableNumber?: string | null },
  ) =>
    request<EventParticipant>(
      `/events/${eventUuid}/participants/${id}/status`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  setRestAfterMatch: (eventUuid: string, id: number, rest: boolean) =>
    request<EventParticipant>(
      `/events/${eventUuid}/participants/${id}/rest-after-match`,
      { method: 'PUT', body: JSON.stringify({ rest }) },
    ),

  updateEntryName: (eventUuid: string, participantId: number, entryName: string) =>
    request<EventParticipant>(`/events/${eventUuid}/participants/${participantId}/entry-name`, {
      method: 'PATCH',
      body: JSON.stringify({ entryName }),
    }),

  // デモ参加者フラグ切替（system-admin 専用）
  updateDemo: (eventUuid: string, participantId: number, isDemo: boolean) =>
    request<EventParticipant>(`/events/${eventUuid}/participants/${participantId}/demo`, {
      method: 'PATCH',
      body: JSON.stringify({ isDemo }),
    }),

  // 順位除外フラグ切替（マッチング・勝敗・レート対象だが、ランキング表示とポイント付与対象外）
  updateExcludeRanking: (eventUuid: string, participantId: number, excludeFromRanking: boolean) =>
    request<EventParticipant>(`/events/${eventUuid}/participants/${participantId}/exclude-ranking`, {
      method: 'PATCH',
      body: JSON.stringify({ excludeFromRanking }),
    }),

  // 引き継ぎ元から一括登録
  inherit: (eventUuid: string, sourceParticipantIds: number[]) =>
    request<EventParticipant[]>(`/events/${eventUuid}/participants/inherit`, {
      method: 'POST',
      body: JSON.stringify({ sourceParticipantIds }),
    }),

  // 一括チェックイン（applied → checked_in、ガンスリongoingは waiting に補正）
  bulkCheckIn: (eventUuid: string, ids: number[]) =>
    request<{ updated: number; skipped: number }>(
      `/events/${eventUuid}/participants/bulk-check-in`,
      { method: 'PATCH', body: JSON.stringify({ ids }) },
    ),

  // 主催者手動繰上（waitlisted → applied、定員無視）
  promoteFromWaitlist: (eventUuid: string, id: number) =>
    request<{ ok: boolean; status: string }>(
      `/events/${eventUuid}/participants/${id}/promote-from-waitlist`,
      { method: 'PATCH' },
    ),

  // プレイヤー切替（playerId を別 Player に差し替え。対戦履歴ゼロ前提）
  changePlayer: (eventUuid: string, id: number, playerId: number) =>
    request<EventParticipant>(
      `/events/${eventUuid}/participants/${id}/player`,
      { method: 'PATCH', body: JSON.stringify({ playerId }) },
    ),

  csvImport: (
    eventUuid: string,
    rows: Array<{ playerName: string; tonamelId?: string; linkedPlayerId?: number }>,
  ) =>
    request<{ imported: number; skipped: number; errors: string[] }>(
      `/events/${eventUuid}/participants/csv-import`,
      { method: 'POST', body: JSON.stringify({ rows }) },
    ),
}

// ── マッチング回避設定（G-12） ───────────────────────────────
export type MatchAvoidancePair = EventMatchAvoidancePair
export type ParticipantConcurrencyGroup = EventParticipantGroup
export type UpdateMatchConstraintsRequest = UpdateParticipantMatchConstraintsRequest

export const matchAvoidanceApi = {
  // request<T> は ApiResponse<T> の data を剥がして返すため、T には envelope ではなく
  // payload 型を渡す（envelope を渡すと二重アンラップで undefined になる）
  listPairs: (eventUuid: string) =>
    request<EventMatchAvoidanceListResponse['data']>(`/events/${eventUuid}/match-avoidances`),

  listGroups: (eventUuid: string) =>
    request<EventParticipantGroupListResponse['data']>(`/events/${eventUuid}/participant-groups`),

  updateConstraints: (eventUuid: string, participantId: number, body: UpdateMatchConstraintsRequest) =>
    request<UpdateParticipantMatchConstraintsResponse['data']>(
      `/events/${eventUuid}/participants/${participantId}/match-constraints`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
}

// ── Matching ─────────────────────────────────────────────────

export type SortKey = 'waitSecs' | 'matchCount' | 'wins' | 'winRate'

export interface MatchingCandidate {
  rank:            number
  participant1:    EventParticipant   // player を include 済み
  participant2:    EventParticipant   // player を include 済み
  waitSecs1:       number
  waitSecs2:       number
  waitSecsSum:     number
  matchCount1:     number
  matchCount2:     number
  hasPlayedBefore: boolean
  suggestedTable:  string | null
  freeTables:      string[]
}

export const matchingApi = {
  candidates: (eventUuid: string, order?: SortKey[]) =>
    request<MatchingCandidate[]>(
      `/events/${eventUuid}/matching/candidates${order?.length ? `?order=${order.join(',')}` : ''}`,
    ),

  confirm: (eventUuid: string, body: CreateMatchRequest) =>
    request<Match>(`/events/${eventUuid}/matching`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cancel: (eventUuid: string, matchId: number) =>
    request<void>(`/events/${eventUuid}/matching/${matchId}`, {
      method: 'DELETE',
    }),

  close: (eventUuid: string) =>
    request<void>(`/events/${eventUuid}/matching/close`, { method: 'POST' }),
}

// ── Matches ──────────────────────────────────────────────────

export const matchesApi = {
  list: (eventUuid: string) =>
    request<Match[]>(`/events/${eventUuid}/matches`),

  finish: (eventUuid: string, matchId: number, body: FinishMatchRequest) =>
    request<Match>(`/events/${eventUuid}/matches/${matchId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  delete: (eventUuid: string, matchId: number) =>
    request<void>(`/events/${eventUuid}/matches/${matchId}`, {
      method: 'DELETE',
    }),
}

// ── Timers ───────────────────────────────────────────────────

export const timerApi = {
  startMatch: (eventUuid: string, matchId: number) =>
    request<Match>(`/events/${eventUuid}/matches/${matchId}/timer/start`, { method: 'POST' }),

  pauseMatch: (eventUuid: string, matchId: number) =>
    request<Match>(`/events/${eventUuid}/matches/${matchId}/timer/pause`, { method: 'POST' }),

  resumeMatch: (eventUuid: string, matchId: number) =>
    request<Match>(`/events/${eventUuid}/matches/${matchId}/timer/resume`, { method: 'POST' }),

  pauseRound: (eventUuid: string, roundId: number) =>
    request<SwissRound | TournamentRound>(`/events/${eventUuid}/rounds/${roundId}/timer/pause`, { method: 'POST' }),

  resumeRound: (eventUuid: string, roundId: number) =>
    request<SwissRound | TournamentRound>(`/events/${eventUuid}/rounds/${roundId}/timer/resume`, { method: 'POST' }),
}

// ── Ranking ──────────────────────────────────────────────────

export const rankingApi = {
  get: (eventUuid: string) =>
    request<RankingEntry[] | LeagueRankingSection[]>(`/events/${eventUuid}/ranking`),
}

// ── Swiss ────────────────────────────────────────────────────

export const swissApi = {
  getRounds: (eventUuid: string) =>
    request<SwissRound[]>(`/events/${eventUuid}/swiss/rounds`),

  createRound: (eventUuid: string) =>
    request<SwissRound>(`/events/${eventUuid}/swiss/rounds`, { method: 'POST' }),

  confirmPairing: (eventUuid: string, roundId: number) =>
    request<SwissRound>(`/events/${eventUuid}/swiss/rounds/${roundId}/confirm-pairing`, { method: 'POST' }),

  startRound: (eventUuid: string, roundId: number) =>
    request<SwissRound>(`/events/${eventUuid}/swiss/rounds/${roundId}/start`, { method: 'POST' }),

  finishRound: (eventUuid: string, roundId: number) =>
    request<SwissRound>(`/events/${eventUuid}/swiss/rounds/${roundId}/finish`, { method: 'POST' }),

  reshuffleRound: (eventUuid: string, roundId: number) =>
    request<SwissRound>(`/events/${eventUuid}/swiss/rounds/${roundId}/reshuffle`, { method: 'POST' }),

  // 順次モード: 待機マッチに卓を割り当てて対戦開始
  assignMatchTable: (eventUuid: string, roundId: number, matchId: number, tableNumber: string) =>
    request<Match>(`/events/${eventUuid}/swiss/rounds/${roundId}/matches/${matchId}/assign-table`, {
      method: 'POST',
      body: JSON.stringify({ tableNumber }),
    }),

  // 順次モード: 開始済みマッチを待機に戻す（対戦順入れ替え運用）
  unassignMatchTable: (eventUuid: string, roundId: number, matchId: number) =>
    request<Match>(`/events/${eventUuid}/swiss/rounds/${roundId}/matches/${matchId}/unassign-table`, {
      method: 'POST',
    }),

  // 順次モード: 待機リスト並び順を更新（遅刻対応）
  reorderPending: (eventUuid: string, roundId: number, matchIds: number[]) =>
    request<{ roundId: number; matchIds: number[] }>(`/events/${eventUuid}/swiss/rounds/${roundId}/pending-order`, {
      method: 'PATCH',
      body: JSON.stringify({ matchIds }),
    }),

  dropParticipant: (eventUuid: string, participantId: number) =>
    request<EventParticipant>(`/events/${eventUuid}/swiss/participants/${participantId}/drop`, { method: 'PUT' }),
}

// ── Tournament ───────────────────────────────────────────────

export const tournamentApi = {
  getRounds: (eventUuid: string) =>
    request<TournamentRound[]>(`/events/${eventUuid}/tournament/rounds`),

  createRound: (eventUuid: string) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds`, { method: 'POST' }),

  confirmPairing: (eventUuid: string, roundId: number) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds/${roundId}/confirm-pairing`, { method: 'POST' }),

  startRound: (eventUuid: string, roundId: number) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds/${roundId}/start`, { method: 'POST' }),

  finishRound: (eventUuid: string, roundId: number) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds/${roundId}/finish`, { method: 'POST' }),

  dropParticipant: (eventUuid: string, participantId: number) =>
    request<EventParticipant>(`/events/${eventUuid}/tournament/participants/${participantId}/drop`, { method: 'PUT' }),

  reshuffleRound: (eventUuid: string, roundId: number) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds/${roundId}/reshuffle`, { method: 'POST' }),

  swapSlots: (eventUuid: string, roundId: number, participantAId: number, participantBId: number) =>
    request<TournamentRound>(`/events/${eventUuid}/tournament/rounds/${roundId}/swap-slots`, {
      method: 'PUT',
      body:   JSON.stringify({ participantAId, participantBId }),
    }),

  // 順次モード: 待機マッチに卓を割り当てて対戦開始
  assignMatchTable: (eventUuid: string, roundId: number, matchId: number, tableNumber: string) =>
    request<Match>(`/events/${eventUuid}/tournament/rounds/${roundId}/matches/${matchId}/assign-table`, {
      method: 'POST',
      body: JSON.stringify({ tableNumber }),
    }),

  // 順次モード: 開始済みマッチを待機に戻す（対戦順入れ替え運用）
  unassignMatchTable: (eventUuid: string, roundId: number, matchId: number) =>
    request<Match>(`/events/${eventUuid}/tournament/rounds/${roundId}/matches/${matchId}/unassign-table`, {
      method: 'POST',
    }),

  // 順次モード: 待機リスト並び順を更新（遅刻対応）
  reorderPending: (eventUuid: string, roundId: number, matchIds: number[]) =>
    request<{ roundId: number; matchIds: number[] }>(`/events/${eventUuid}/tournament/rounds/${roundId}/pending-order`, {
      method: 'PATCH',
      body: JSON.stringify({ matchIds }),
    }),
}

// ── Auth ─────────────────────────────────────────────────────

export const authApi = {
  me: () => request<User & {
    isOrganizer: boolean
    hasSetPlayerName: boolean
    initialPlayerName: string
    orgMemberships: { organizationId: number; role: 'OWNER' | 'MEMBER' | 'RESULT_ONLY' }[]
  }>('/auth/me'),
  updateMe: (name: string) =>
    request<User & { token: string }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),
  deleteMe: () =>
    request<{ ok: true }>('/auth/me', { method: 'DELETE' }),
  // 初回登録時のプレイヤー名入力（/welcome ページから呼ぶ）
  setInitialPlayerName: (name: string) =>
    request<{ ok: true; name: string }>('/auth/me/initial-player-name', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  registerAsOrganizer: () =>
    request<{ ok: true; alreadyRegistered: boolean; organizationId: number; organizationName: string | null }>(
      '/auth/me/organizer-registration',
      { method: 'POST' },
    ),
  // 自分が認証済みにしている Player 一覧（マイページ切替UI用）
  myPlayers: () =>
    request<{ id: number; name: string; discriminator: string; myPageToken: string; avatarUrl: string | null; avatarUpdatedAt: string | null }[]>('/auth/me/players'),
  // 自分の所有 Player が参加している ongoing/closing イベント（アバターメニュー用）
  activeEvents: () =>
    request<MeActiveEvent[]>('/auth/me/active-events'),
  requestOtp: (email: string, name?: string) =>
    request<{ ok: true }>('/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify(name ? { email, name } : { email }),
    }),
  verifyOtp: (email: string, code: string, origin?: 'admin' | 'player') =>
    request<{ token: string }>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify(origin ? { email, code, origin } : { email, code }),
    }),
  // 既ログインユーザの Discord 連携開始（連携先 userId は cookie 経由でサーバが識別）
  discordLinkStart: () =>
    request<{ authorizeUrl: string }>('/auth/discord/link/start', { method: 'POST' }),

  // 既ログインユーザの LINE 連携開始
  lineLinkStart: () =>
    request<{ authorizeUrl: string }>('/auth/line/link/start', { method: 'POST' }),

  // 既ログインユーザの Google 連携開始
  googleLinkStart: () =>
    request<{ authorizeUrl: string }>('/auth/google/link/start', { method: 'POST' }),

  // 既存ユーザがメアドを追加/変更（OTP）
  emailStart: (email: string) =>
    request<{ ok: true; alreadySet?: boolean }>('/auth/me/email/start', {
      method: 'POST',
      body:   JSON.stringify({ email }),
    }),
  emailVerify: (email: string, code: string) =>
    request<{ ok: true }>('/auth/me/email/verify', {
      method: 'POST',
      body:   JSON.stringify({ email, code }),
    }),

  // 連携状況の取得（LoginProvidersSection 用）
  myProviders: () =>
    request<{
      email:   string | null
      google:  { connected: boolean; identifier: string | null }
      discord: { connected: boolean; identifier: string | null }
      line:    { connected: boolean; identifier: string | null }
    }>('/auth/me/providers'),

  // 認証手段の連携解除（最後の手段は 403 LastAuthMethod）
  unlinkProvider: (provider: 'google' | 'discord' | 'line') =>
    request<void>(`/auth/me/providers/${provider}`, { method: 'DELETE' }),
}

// ── Players (マスタ) ─────────────────────────────────────────

export const playersApi = {
  list: (opts?: { includeHidden?: boolean }) =>
    request<OrgPlayerEntry[]>(`/players${opts?.includeHidden ? '?includeHidden=true' : ''}`),
  search: (q: string) =>
    request<Player[]>(`/players/search?q=${encodeURIComponent(q)}`),
  // TAIARI ID（discriminator）で全システム検索
  lookup: (discriminator: string) =>
    request<Player>(`/players/lookup?discriminator=${encodeURIComponent(discriminator)}`),
  getDetail: (id: number) =>
    request<PlayerDetail>(`/players/${id}`),
  getByToken: (myPageToken: string) =>
    request<Player>(`/players/by-token/${myPageToken}`),
  setHidden: (id: number, hidden: boolean) =>
    request<{ hidden: boolean }>(`/players/${id}/hidden`, {
      method: 'PUT',
      body: JSON.stringify({ hidden }),
    }),
  // 主催者によるアバター削除（モデレーション）
  deleteAvatar: (id: number) =>
    request<{ avatarUrl: null; avatarUpdatedAt: null }>(`/players/${id}/avatar`, {
      method: 'DELETE',
    }),
}

// ── Player page (QRトークン認証) ─────────────────────────────

// プレイヤー向け fetch
// - JWT が localStorage にあれば Bearer で送る（保護済マイページの認可で必要）
// - 401 / 403 でも自動リダイレクトしない（呼び出し側でハンドリング）
async function playerRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })
  consumeRefreshToken(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.message ?? res.statusText, { remainingSecs: body.remainingSecs })
  }
  if (res.status === 204) return undefined as T
  const json: ApiResponse<T> = await res.json()
  return json.data
}

const P = (token: string) => `/p/events/${token}`

// ── 申込ページ（Phase 2） ───────────────────────────────────
export const signupApi = {
  /// 申込ページ表示用データ取得（公開・JWT があれば myEntries も返る）
  get: (eventUuid: string) =>
    playerRequest<SignupPageData>(`/p/signup/${eventUuid}`),

  /// 申込実行（認証必須）。playerId 省略時は ownedPlayers が1件のときのみ自動採用
  signup: (eventUuid: string, body: { playerId?: number } = {}) =>
    playerRequest<{ status: string; number: number; playerId: number }>(`/p/signup/${eventUuid}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /// 自主キャンセル（認証必須）
  cancel: (eventUuid: string, body: { playerId?: number } = {}) =>
    playerRequest<{ ok: boolean; playerId: number }>(`/p/signup/${eventUuid}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),

  /// チェックイン実行（認証必須）。複数候補なら needsSelection=true で candidates を返す
  checkin: (eventUuid: string, body: { playerId?: number } = {}) =>
    playerRequest<CheckinResponse>(`/p/events/${eventUuid}/checkin`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /// 未認証チェックイン（プレイヤー名のみで実行）
  /// 第1段: { name } で候補一覧 or 直接成功を取得
  /// 第2段: { name, participantId } で既存参加者として確定 / { name, walkIn: true } で新規walkIn確定
  checkinAnonymous: (
    eventUuid: string,
    body: { name: string; participantId?: number; walkIn?: boolean },
  ) =>
    playerRequest<CheckinAnonymousResponse>(`/p/events/${eventUuid}/checkin-anonymous`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}

/// チェックイン API のレスポンス
export type CheckinResponse =
  | {
      ok: true
      alreadyCheckedIn: boolean
      walkIn: boolean   // 申込なしの飛び込みチェックインで新規エントリーされた場合 true
      status: string
      number: number
      playerId: number
      playerName: string
      discriminator: string
      eventName: string
      myPageToken: string | null
      participantToken: string | null  // /event/p/:token 直行用（旧データで null の場合あり）
    }
  | {
      needsSelection: true
      candidates: {
        playerId: number
        name: string
        discriminator: string
        status: string | null  // walkIn=true のときは null
        walkIn: boolean        // true=申込なしの飛び込み候補
      }[]
    }

/// 未認証チェックイン API のレスポンス
export type CheckinAnonymousResponse =
  | {
      ok: true
      alreadyCheckedIn: boolean
      walkIn: boolean
      status: string
      number: number
      playerId: number
      playerName: string
      discriminator: string
      eventName: string
      myPageToken: string | null
      participantToken: string | null
    }
  | {
      needsSelection: true
      allowWalkIn: boolean              // true なら末尾に「新規参加として登録」オプションを出す
      inputName: string                  // 入力された名前（walkIn 確定時にそのまま返送する用）
      candidates: {
        participantId: number            // EventParticipant.id（未認証フローでは playerId ではなくこちら）
        name: string
        discriminator: string
        status: string
      }[]
    }

export const playerApi = {
  getEventInfo: (token: string) =>
    playerRequest<PlayerEventInfo>(`${P(token)}`),

  /// カードQRスキャンで当日受付。複数候補なら needsSelection=true を返す
  /// 未認証 + 飛び込み(B-2)の場合は { name } を渡して新規 Player 作成
  checkinByToken: (token: string, body: { playerId?: number; name?: string } = {}) =>
    playerRequest<CheckinResponse>(`/p/events/by-token/${token}/checkin`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getParticipants: (token: string) =>
    playerRequest<EventParticipant[]>(`${P(token)}/participants`),

  getMatches: (token: string) =>
    playerRequest<Match[]>(`${P(token)}/matches`),

  getCoinTransactions: (token: string, params: { limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    return playerRequest<CoinTransactionListResponse>(`${P(token)}/coin-transactions${qs ? `?${qs}` : ''}`)
  },

  getBenefitAwards: (token: string, params: { limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    return playerRequest<BenefitAwardListResponse>(`${P(token)}/benefit-awards${qs ? `?${qs}` : ''}`)
  },

  transferCoins: (token: string, body: CoinTransferRequest) =>
    playerRequest<CoinTransferResponse>(`${P(token)}/coin-transfers`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getCoinTransferCandidates: (token: string) =>
    playerRequest<CoinTransferCandidate[]>(`${P(token)}/coin-transfer-candidates`),

  getCoinBetProposals: (token: string) =>
    playerRequest<CoinBetProposalListResponse>(`${P(token)}/coin-bet-proposals`),

  createCoinBetProposal: (token: string, body: SelfServeMatchCreateRequest & { betAmount: number }) =>
    playerRequest<CoinBetProposal>(`${P(token)}/coin-bet-proposals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createCoinBetSession: (token: string, body: SelfServeMatchCreateRequest) =>
    playerRequest<CoinBetProposal>(`${P(token)}/coin-bet-sessions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  respondCoinBetProposal: (token: string, proposalId: number, action: 'accept' | 'decline' | 'cancel') =>
    playerRequest<Match | CoinBetProposal>(`${P(token)}/coin-bet-proposals/${proposalId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  respondCoinBetLiveAction: (token: string, proposalId: number, body: CoinBetLiveActionRequest) =>
    playerRequest<Match | CoinBetProposal>(`${P(token)}/coin-bet-proposals/${proposalId}/live-action`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getSelfServeMatchCandidates: (token: string) =>
    playerRequest<SelfServeMatchCandidate[]>(`${P(token)}/self-serve-match-candidates`),

  // QRマッチング（副動線）: QR提示=本人合意とみなし、非コインは承認なしで即時成立する
  previewSelfServeMatch: (token: string, body: SelfServeMatchCreateRequest) =>
    playerRequest<SelfServeMatchPreviewResponse>(`${P(token)}/self-serve-matches/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createSelfServeMatch: (token: string, body: SelfServeMatchCreateRequest) =>
    playerRequest<Match>(`${P(token)}/self-serve-matches`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getSelfServeMatchRequests: (token: string) =>
    playerRequest<SelfServeMatchRequestListResponse>(`${P(token)}/self-serve-match-requests`),

  createSelfServeMatchRequest: (token: string, opponentParticipantId: number) =>
    playerRequest<SelfServeMatchRequest>(`${P(token)}/self-serve-match-requests`, {
      method: 'POST', body: JSON.stringify({ opponentParticipantId }),
    }),

  respondSelfServeMatchRequest: (token: string, requestId: number, action: 'accept' | 'decline' | 'cancel') =>
    playerRequest<Match | SelfServeMatchRequest>(`${P(token)}/self-serve-match-requests/${requestId}/respond`, {
      method: 'POST', body: JSON.stringify({ action }),
    }),

  previewCoinTransfer: (token: string, body: { opponentToken?: string; opponentParticipantId?: number }) =>
    playerRequest<CoinTransferPreviewResponse>(`${P(token)}/coin-transfers/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getLeagues: (token: string) =>
    playerRequest<{ id: number; name: string }[]>(`${P(token)}/leagues`),

  getRanking: (token: string) =>
    playerRequest<(RankingEntry | LeagueRankingSection)[]>(`${P(token)}/ranking`),

  getMyMatches: (token: string) =>
    playerRequest<PlayerMatch[]>(`${P(token)}/my-matches`),

  saveMemo: (token: string, matchId: number, memo: string) =>
    playerRequest<void>(`${P(token)}/my-matches/${matchId}/memo`, {
      method: 'PUT',
      body: JSON.stringify({ memo }),
    }),

  submitResult: (token: string, matchId: number, body: { result: string; player1Score?: number; player2Score?: number }) =>
    playerRequest<import('../../../shared/types').Match>(`${P(token)}/matches/${matchId}/result`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  ready: (token: string, matchId: number) =>
    playerRequest<{ matchId: number; player1ReadyAt: string | null; player2ReadyAt: string | null; startedAt: string | null; bothReady?: boolean; idempotent: boolean }>(
      `${P(token)}/matches/${matchId}/ready`,
      { method: 'POST' },
    ),

  confirmResult: (token: string, matchId: number) =>
    playerRequest<import('../../../shared/types').Match>(`${P(token)}/matches/${matchId}/result/confirm`, {
      method: 'POST',
    }),

  markCoinLuckyBonusViewed: (token: string, matchId: number) =>
    playerRequest<import('../../../shared/types').Match>(`${P(token)}/matches/${matchId}/coin-lucky-bonus/viewed`, {
      method: 'POST',
    }),

  getLuckyRouletteDraws: (token: string, params: { unviewedOnly?: boolean; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    if (params.unviewedOnly !== undefined) qs.set('unviewedOnly', params.unviewedOnly ? 'true' : 'false')
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString()
    return playerRequest<LuckyRouletteDrawListResponse>(`${P(token)}/lucky-roulette-draws${query ? `?${query}` : ''}`)
  },

  markLuckyRouletteDrawViewed: (token: string, drawId: number) =>
    playerRequest<import('../../../shared/types').LuckyRouletteDrawEntry>(`${P(token)}/lucky-roulette-draws/${drawId}/viewed`, {
      method: 'POST',
    }),

  resolveLuckyRouletteDraw: (token: string, drawId: number) =>
    playerRequest<import('../../../shared/types').LuckyRouletteDrawEntry>(`${P(token)}/lucky-roulette-draws/${drawId}/resolve`, {
      method: 'POST',
    }),

  rejectResult: (token: string, matchId: number) =>
    playerRequest<import('../../../shared/types').Match>(`${P(token)}/matches/${matchId}/result/reject`, {
      method: 'POST',
    }),

  sendTaiari: (token: string, matchId: number) =>
    playerRequest<void>(`${P(token)}/my-matches/${matchId}/taiari`, {
      method: 'POST',
    }),

  setStatus: (token: string, resting: boolean) =>
    playerRequest<EventParticipant>(`${P(token)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ resting }),
    }),

  setRestAfterMatch: (token: string, rest: boolean) =>
    playerRequest<EventParticipant>(`${P(token)}/rest-after-match`, {
      method: 'PUT',
      body: JSON.stringify({ rest }),
    }),

  updateEntryName: (token: string, entryName: string) =>
    playerRequest<EventParticipant>(`${P(token)}/entry-name`, {
      method: 'PATCH',
      body: JSON.stringify({ entryName }),
    }),

  getRounds: (token: string) =>
    playerRequest<SwissRound[]>(`${P(token)}/rounds`),
}

// ── MyPage（Player.ownerUserId が NULL なら認証不要 / IS NOT NULL なら JWT 必須） ─────

export const myPageApi = {
  get: (token: string) =>
    playerRequest<PlayerMyPageData>(`/my-page/${token}`),

  getEvents: (token: string, offset: number) =>
    playerRequest<PlayerMyPageEventsPage>(`/my-page/${token}/events?offset=${offset}`),

  getPointRankings: (token: string) =>
    playerRequest<{ rankings: PointRanking[] }>(`/my-page/${token}/point-rankings`),

  generateQr: (myPageToken: string) =>
    playerRequest<{ token: string; expiresAt: string }>(`/my-page/${myPageToken}/qr`, {
      method: 'POST',
    }),

  updatePlayerName: (myPageToken: string, name: string) =>
    playerRequest<{ id: number; name: string; discriminator: string }>(`/my-page/${myPageToken}/player-name`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  // 認証済みにする（保護）— JWT 必須
  protect: (myPageToken: string) =>
    playerRequest<{ ownerUserId: number }>(`/my-page/${myPageToken}/protect`, {
      method: 'POST',
    }),

  // 認証を解除（自分が所有者・主催者権限なしのとき）— JWT 必須
  unprotect: (myPageToken: string) =>
    playerRequest<{ ownerUserId: null }>(`/my-page/${myPageToken}/protect`, {
      method: 'DELETE',
    }),

  mergeContext: (targetMyPageToken: string) =>
    playerRequest<PlayerMergeContext>(`/my-page/${targetMyPageToken}/merge-context`),

  previewMerge: (targetMyPageToken: string, keepMyPageToken: string) =>
    playerRequest<PlayerMergePreview>(`/my-page/${targetMyPageToken}/merge-preview`, {
      method: 'POST',
      body: JSON.stringify({ keepMyPageToken }),
    }),

  executeMerge: (targetMyPageToken: string, keepMyPageToken: string) =>
    playerRequest<PlayerMergeResult>(`/my-page/${targetMyPageToken}/merge`, {
      method: 'POST',
      body: JSON.stringify({ keepMyPageToken }),
    }),

  // プレイヤー情報を匿名化削除（未認証 Player のみ）— 認証なしでも myPageToken さえあれば実行可
  deletePlayer: (myPageToken: string) =>
    playerRequest<{ ok: true }>(`/my-page/${myPageToken}`, {
      method: 'DELETE',
    }),

  // アバター画像アップロード（multipart/form-data・JWT 必須）
  uploadAvatar: async (myPageToken: string, file: Blob, filename = 'avatar.webp'): Promise<{ avatarUrl: string; avatarUpdatedAt: string }> => {
    const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
    const fd = new FormData()
    fd.append('file', file, filename)
    const res = await fetch(`${BASE}/my-page/${myPageToken}/avatar`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        // Content-Type は FormData が自動付与（boundary 込み）
      },
      body: fd,
    })
    consumeRefreshToken(res)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? res.statusText)
    }
    const json: ApiResponse<{ avatarUrl: string; avatarUpdatedAt: string }> = await res.json()
    return json.data
  },

  // アバター画像削除（自分自身・JWT 必須）
  deleteAvatar: (myPageToken: string) =>
    playerRequest<{ avatarUrl: null; avatarUpdatedAt: null }>(`/my-page/${myPageToken}/avatar`, {
      method: 'DELETE',
    }),

  // ランクマッチ: アクティブシーズン一覧 + 参加状態
  getRankMatch: (myPageToken: string) =>
    playerRequest<PlayerRankMatchStatus[]>(`/my-page/${myPageToken}/rank-match`),

  // ランクマッチ: シーズンに参加登録（認証済みプレイヤーのみ）
  joinRankMatchSeason: (myPageToken: string, seasonId: number) =>
    playerRequest<{ seasonId: number; rating: number }>(`/my-page/${myPageToken}/rank-match/${seasonId}/join`, {
      method: 'POST',
    }),

  // ランクマッチ: シーズン別リーダーボード（上位20+自分の行+次回更新タイミング）
  getRankMatchLeaderboard: (myPageToken: string, seasonId: number) =>
    playerRequest<RankMatchLeaderboardResponse>(
      `/my-page/${myPageToken}/rank-match/${seasonId}/leaderboard`,
    ),

  // ランクマッチ: シーズン対象イベント一覧
  getRankMatchSeasonEvents: (myPageToken: string, seasonId: number) =>
    playerRequest<RankMatchSeasonEventEntry[]>(
      `/my-page/${myPageToken}/rank-match/${seasonId}/events`,
    ),
}

// ── Point Categories ─────────────────────────────────────────

export const pointCategoriesApi = {
  list: () =>
    request<PointCategory[]>('/point-categories'),

  create: (body: { name: string; showInRanking?: boolean }) =>
    request<PointCategory>('/point-categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (id: number, body: { name?: string; showInRanking?: boolean; isActive?: boolean }) =>
    request<PointCategory>(`/point-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  delete: (id: number) =>
    request<void>(`/point-categories/${id}`, { method: 'DELETE' }),

  addRule: (categoryId: number, ruleId: number) =>
    request<PointCategory>(`/point-categories/${categoryId}/rules`, {
      method: 'POST',
      body: JSON.stringify({ ruleId }),
    }),

  removeRule: (categoryId: number, ruleId: number) =>
    request<PointCategory>(`/point-categories/${categoryId}/rules/${ruleId}`, {
      method: 'DELETE',
    }),

  getRankings: (categoryId: number) =>
    request<{ rankings: CategoryRankingEntry[] }>(`/point-categories/${categoryId}/rankings`),
}

// ── Point Rules ──────────────────────────────────────────────

export interface CreatePointRuleTierRequest {
  thresholdType: 'FLAT' | 'RANK' | 'WINS' | 'MATCHES'
  thresholdMin?: number | null
  thresholdMax?: number | null
  points: number
}

export const pointRulesApi = {
  list: () =>
    request<PointRule[]>('/point-rules'),

  create: (body: { name: string; tiers: CreatePointRuleTierRequest[] }) =>
    request<PointRule>('/point-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (id: number, body: { name?: string; tiers?: CreatePointRuleTierRequest[] }) =>
    request<PointRule>(`/point-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  delete: (id: number) =>
    request<void>(`/point-rules/${id}`, { method: 'DELETE' }),
}

// ── Private Matches（公開・認証不要） ─────────────────────────

export const privateMatchApi = {
  get: (matchId: number, myPageToken: string) =>
    playerRequest<PrivateMatch>(`/pm/${matchId}?myPageToken=${encodeURIComponent(myPageToken)}`),

  start: (myPageToken: string, playerQrToken: string) =>
    playerRequest<PrivateMatch>('/pm/start', {
      method: 'POST',
      body: JSON.stringify({ myPageToken, playerQrToken }),
    }),

  inputResult: (
    matchId: number,
    myPageToken: string,
    result: string,
    score1?: string,
    score2?: string,
  ) =>
    playerRequest<PrivateMatch>(`/pm/${matchId}/result`, {
      method: 'POST',
      body: JSON.stringify({ myPageToken, result, score1, score2 }),
    }),

  confirm: (matchId: number, myPageToken: string) =>
    playerRequest<PrivateMatch>(`/pm/${matchId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ myPageToken }),
    }),

  reject: (matchId: number, myPageToken: string) =>
    playerRequest<PrivateMatch>(`/pm/${matchId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ myPageToken }),
    }),

  sendTaiari: (matchId: number, myPageToken: string) =>
    playerRequest<void>(`/pm/${matchId}/taiari`, {
      method: 'POST',
      body: JSON.stringify({ myPageToken }),
    }),

  list: (myPageToken: string, offset = 0) =>
    playerRequest<PrivateMatchPage>(`/pm/player/${myPageToken}?offset=${offset}`),

  listByDate: (myPageToken: string, date: string) =>
    playerRequest<PrivateMatch[]>(`/pm/player/${myPageToken}/date/${date}`),
}

// ── Leagues ──────────────────────────────────────────────────

export interface SaveLeaguesRequest {
  leagues: { name: string; participantIds: number[] }[]
}

export const leaguesApi = {
  get: (uuid: string) =>
    request<LeagueWithParticipants[]>(`/events/${uuid}/leagues`),

  save: (uuid: string, body: SaveLeaguesRequest) =>
    request<LeagueWithParticipants[]>(`/events/${uuid}/leagues`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
}

// ── Organizations ────────────────────────────────────────────

export const organizationsApi = {
  getMyOrgs: () =>
    request<OrganizationDetail[]>('/organizations/me'),

  updateName: (orgId: number, name: string) =>
    request<{ id: number; name: string }>(`/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),

  updateBranding: (orgId: number, body: UpdateOrganizationBrandingRequest) =>
    request<OrganizationBranding>(`/organizations/${orgId}/branding`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ロゴ画像アップロード（multipart/form-data・JWT 必須）。更新後の branding を返す
  uploadLogo: async (orgId: number, file: Blob, filename = 'logo.webp'): Promise<OrganizationBranding> => {
    const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
    const fd = new FormData()
    fd.append('file', file, filename)
    const res = await fetch(`${BASE}/organizations/${orgId}/logo`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        // Content-Type は FormData が自動付与（boundary 込み）
      },
      body: fd,
    })
    consumeRefreshToken(res)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? res.statusText)
    }
    const json: ApiResponse<OrganizationBranding> = await res.json()
    return json.data
  },

  // ロゴのクロップ済み画像更新（原本は維持）。cropRect は原本座標系
  cropLogo: async (
    orgId: number,
    file: Blob,
    cropRect: OrganizationLogoCropRect,
    filename = 'logo-cropped.webp',
  ): Promise<OrganizationBranding> => {
    const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
    const fd = new FormData()
    fd.append('cropRect', JSON.stringify(cropRect))
    fd.append('file', file, filename)
    const res = await fetch(`${BASE}/organizations/${orgId}/logo/crop`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'X-Logo-Crop-Rect': JSON.stringify(cropRect),
      },
      body: fd,
    })
    consumeRefreshToken(res)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? res.statusText)
    }
    const json: ApiResponse<OrganizationBranding> = await res.json()
    return json.data
  },

  // ロゴ画像削除（JWT 必須）。更新後の branding を返す
  deleteLogo: (orgId: number) =>
    request<OrganizationBranding>(`/organizations/${orgId}/logo`, { method: 'DELETE' }),

  addInvite: (orgId: number, email: string, role: 'MEMBER' | 'RESULT_ONLY' = 'MEMBER') =>
    request<OrganizationInvite>(`/organizations/${orgId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  removeInvite: (orgId: number, inviteId: number) =>
    request<void>(`/organizations/${orgId}/invites/${inviteId}`, { method: 'DELETE' }),

  /** 招待メールの受諾リンク（/invite?org=N）から呼ぶ。ログイン中ならその場でメンバーになる */
  acceptInvite: (orgId: number) =>
    request<{
      status:           'accepted' | 'already_member'
      organizationId:   number
      organizationName: string
      role:             'OWNER' | 'MEMBER' | 'RESULT_ONLY'
    }>(`/organizations/${orgId}/invites/accept`, { method: 'POST' }),

  removeMember: (orgId: number, userId: number) =>
    request<void>(`/organizations/${orgId}/members/${userId}`, { method: 'DELETE' }),

  /** メンバーのロール変更(MEMBER ↔ RESULT_ONLY)。OWNER+MEMBER 可、target が OWNER の場合は不可 */
  updateMemberRole: (orgId: number, userId: number, role: 'MEMBER' | 'RESULT_ONLY') =>
    request<{ userId: number; role: string }>(`/organizations/${orgId}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  transferOwnership: (orgId: number, newOwnerId: number) =>
    request<void>(`/organizations/${orgId}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    }),

  // ── Discord 連携 ──

  /** Discord OAuth フロー開始: 認証 URL を返してもらい window.location で遷移する */
  startDiscordOAuth: (orgId: number) =>
    request<{ authorizeUrl: string }>(`/auth/discord/start`, {
      method: 'POST',
      body: JSON.stringify({ orgId }),
    }),

  /** Discord 連携を解除（discordGuildId と discordDefaultAnnounceChannelId を null に） */
  disconnectDiscord: (orgId: number) =>
    request<void>(`/organizations/${orgId}/discord/disconnect`, { method: 'POST' }),

  /** お知らせチャンネル設定（null = 自動作成、文字列 = 既存チャンネル ID） */
  setDiscordAnnounceChannel: (orgId: number, channelId: string | null) =>
    request<{ discordDefaultAnnounceChannelId: string | null }>(`/organizations/${orgId}/discord/announce-channel`, {
      method: 'PUT',
      body: JSON.stringify({ channelId }),
    }),

  /** チャンネル ID に Bot からテストメッセージを送信して書き込み可否を確認 */
  testDiscordAnnounceChannel: (orgId: number, channelId: string) =>
    request<{ ok: boolean; error?: string }>(`/organizations/${orgId}/discord/announce-channel/test`, {
      method: 'POST',
      body: JSON.stringify({ channelId }),
    }),
}

// ── System Admin ─────────────────────────────────────────────

export const systemApi = {
  listUsers: () =>
    request<SystemAdminUserEntry[]>('/system/users'),

  listOrganizations: () =>
    request<SystemAdminOrgEntry[]>('/system/organizations'),

  listLiveEvents: () =>
    request<SystemAdminLiveEvent[]>('/system/live-events'),

  listUpcomingEvents: () =>
    request<SystemAdminUpcomingEvent[]>('/system/upcoming-events'),

  getUser: (id: number) =>
    request<SystemAdminOrganizerDetail>(`/system/users/${id}`),

  setBan: (id: number, isBanned: boolean) =>
    request<{ id: number; isBanned: boolean }>(`/system/users/${id}/ban`, {
      method: 'PATCH',
      body: JSON.stringify({ isBanned }),
    }),

  setPlan: (id: number, plan: string) =>
    request<{ id: number; plan: string }>(`/system/users/${id}/plan`, {
      method: 'PATCH',
      body: JSON.stringify({ plan }),
    }),

  removeOrgMember: (orgId: number, userId: number) =>
    request<void>(`/system/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
    }),

  // 空の団体を削除（メンバー0・イベント0・参加者0 のみ実行可・409 で空でない旨返却）
  deleteEmptyOrganization: (orgId: number) =>
    request<void>(`/system/organizations/${orgId}`, {
      method: 'DELETE',
    }),

  listGameTitles: () =>
    request<GameTitle[]>('/system/game-titles'),

  addGameTitle: (name: string) =>
    request<GameTitle>('/system/game-titles', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  toggleGameTitle: (id: number, isActive: boolean) =>
    request<GameTitle>(`/system/game-titles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    }),

  listRankMatchSeasons: () =>
    request<RankMatchSeason[]>('/system/rank-match-seasons'),

  createRankMatchSeason: (data: RankMatchSeasonCreateRequest) =>
    request<RankMatchSeason>('/system/rank-match-seasons', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRankMatchSeason: (id: number, data: RankMatchSeasonUpdateRequest) =>
    request<RankMatchSeason>(`/system/rank-match-seasons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // サムネイル画像アップロード（multipart/form-data）
  uploadRankMatchSeasonThumbnail: async (id: number, file: Blob, filename = 'thumbnail.webp'): Promise<RankMatchSeason> => {
    const token = safeLocalStorage.getItem(AUTH_TOKEN_KEY)
    const fd = new FormData()
    fd.append('file', file, filename)
    const res = await fetch(`${BASE}/system/rank-match-seasons/${id}/thumbnail`, {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: fd,
    })
    consumeRefreshToken(res)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? res.statusText)
    }
    const json: ApiResponse<RankMatchSeason> = await res.json()
    return json.data
  },

  deleteRankMatchSeasonThumbnail: (id: number) =>
    request<RankMatchSeason>(`/system/rank-match-seasons/${id}/thumbnail`, {
      method: 'DELETE',
    }),

  // シーズン物理削除（cascade で participants/ratings/logs を消す。Event.rankMatchSeasonId は SetNull）
  deleteRankMatchSeason: (id: number) =>
    request<{
      deletedSeasonId: number
      name:            string
      cleared: { participants: number; ratings: number; logs: number; events: number }
    }>(`/system/rank-match-seasons/${id}`, { method: 'DELETE' }),

  // シーズン参加プレイヤー一覧（レート参考情報付き）
  listRankMatchSeasonPlayers: (id: number) =>
    request<RankMatchSeasonPlayersResponse>(`/system/rank-match-seasons/${id}/players`),
}

// ── Rank Match (主催者向け) ──────────────────────────────────

export const rankMatchApi = {
  listGameTitles: () =>
    request<Pick<GameTitle, 'id' | 'name' | 'isPreset'>[]>('/rank-match/game-titles'),

  upsertGameTitle: (name: string) =>
    request<Pick<GameTitle, 'id' | 'name' | 'isPreset'>>('/rank-match/game-titles', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  listSeasons: () =>
    request<RankMatchSeasonSummary[]>('/rank-match/seasons'),
}

// ── Participants (管理者) ─────────────────────────────────────

export const participantTokenApi = {
  generateToken: (eventUuid: string, participantId: number) =>
    request<{ token: string }>(`/events/${eventUuid}/participants/${participantId}/token`, {
      method: 'POST',
    }),

  getMaxIssuedNumber: (eventUuid: string) =>
    request<{ maxNumber: number }>(`/events/${eventUuid}/qr-tokens/max-issued-number`),

  bulkGenerate: (eventUuid: string, numbers: number[]) =>
    request<{ number: number; token: string; playerName: string | null; entryName: string | null }[]>(
      `/events/${eventUuid}/qr-tokens/generate`,
      { method: 'POST', body: JSON.stringify({ numbers }) },
    ),
}
